// preloadAssets.js
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader'; // Import DRACOLoader
import { EquirectangularReflectionMapping } from 'three';
import { TextureLoader } from 'three';

export async function preloadAssets(room) {
  const gltfLoader = new GLTFLoader();
  const dracoLoader = new DRACOLoader(); // Create a DRACOLoader instance
  // Set the path to Draco decoder files (this is essential for DRACOLoader to work)
  dracoLoader.setDecoderPath('https://cdn.jsdelivr.net/npm/three@0.153.0/examples/jsm/libs/draco/gltf/');
  gltfLoader.setDRACOLoader(dracoLoader); // Attach DRACOLoader to GLTFLoader

  const textureLoader = new TextureLoader();

  // Safely check if config and rooms are defined
  if (!room) {
    console.error("Invalid config structure or no rooms found in config.");
    return Promise.reject("Invalid config structure or no rooms found.");
  }

  // Models are in `public` folder, accessible via `/models/`
  const modelPromises = room.models
    ? room.models.map((modelPath) =>
      new Promise((resolve, reject) => {
        gltfLoader.load(
          `/${modelPath}`, // Adjusted path for models in public folder
          resolve,
          undefined,
          (error) => {
            console.error(`Failed to load model: ${modelPath}`, error);
            reject(`Failed to load model: ${modelPath}`);
          }
        );
      })
    )
    : [];

// In the environmentPromises section:
const environmentPromises = room.environments
  ? room.environments.map((envPath) =>
    new Promise((resolve, reject) => {
      textureLoader.load(
        require(`../assets/${envPath}`),
        (texture) => {
          texture.mapping = EquirectangularReflectionMapping;
          texture.generateMipmaps = true;
          texture.minFilter = THREE.LinearMipmapLinearFilter;
          texture.magFilter = THREE.LinearFilter;
          texture.needsUpdate = true;
          resolve(texture);
        },
        undefined,
        (error) => {
          console.error(`Failed to load environment map: ${envPath}`, error);
          reject(`Failed to load environment map: ${envPath}`);
        }
      );
    })
  )
  : [];

  const swatchPromises = room.swatches
    ? room.swatches.map((swatchPath) =>
      new Promise((resolve, reject) => {
        textureLoader.load(
          require(`../assets/${swatchPath}`), // Dynamic import using `require` for src/assets
          resolve,
          undefined,
          (error) => {
            console.error(`Failed to load swatch: ${swatchPath}`, error);
            reject(`Failed to load swatch: ${swatchPath}`);
          }
        );
      })
    )
    : [];

  const texturePromises = room.textures
    ? room.textures.map((texturePath) =>
      new Promise((resolve, reject) => {
        textureLoader.load(
          require(`../assets/${texturePath}`), // Dynamic import using `require` for src/assets
          resolve,
          undefined,
          (error) => {
            console.error(`Failed to load texture: ${texturePath}`, error);
            reject(`Failed to load texture: ${texturePath}`);
          }
        );
      })
    )
    : [];

  try {
    const [models, environments, swatches, textures] = await Promise.all([
      Promise.all(modelPromises),
      Promise.all(environmentPromises),
      Promise.all(swatchPromises),
      Promise.all(texturePromises)
    ]);

    return [models, environments, swatches, textures];
  } catch (error) {
    console.error('Error loading assets:', error);
    throw error;
  }
}
